import { motion } from "framer-motion";
import { ABOUT } from "@/content";
import { Home, Ruler, ShieldCheck } from "lucide-react";

const STAT_ICONS = [Home, Ruler, ShieldCheck];

export default function About() {
  return (
    <section
      id="about"
      data-testid="section-about"
      className="relative py-16 lg:py-20 bg-brand-cream overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(194,166,116,0.16),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.52),transparent)]" />
      <svg
        className="absolute -top-20 -end-32 lg:-end-10 w-[420px] lg:w-[640px] h-auto opacity-[0.07] pointer-events-none"
        viewBox="0 0 200 240"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M100 5 L195 60 V210 H5 V60 Z M100 35 L170 75 V190 H30 V75 Z"
          stroke="#566351"
          strokeWidth="1"
          fill="none"
        />
        <path
          d="M70 200 V115 L100 95 L130 115 V200"
          stroke="#C2A674"
          strokeWidth="1"
          fill="none"
        />
      </svg>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6 order-2 lg:order-1"
          >
            <span
              className="block text-xs text-brand-accent mb-3 uppercase font-lulo"
              data-testid="about-kicker"
            >
              {ABOUT.kicker}
            </span>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl text-brand-ink mb-5 leading-[1.2]"
              data-testid="about-headline"
            >
              {ABOUT.headline}
            </h2>
            <div className="space-y-3 text-sm lg:text-base text-brand-mute leading-loose max-w-xl">
              {ABOUT.body.map((p, i) => (
                <p key={i} data-testid={`about-paragraph-${i}`}>
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-6 inline-flex items-center gap-3 text-brand-ink">
              <div className="h-px w-12 bg-brand-accent" />
              <span className="text-base text-brand-olive">
                مُيسر — اسم تثق به في عالم العقار
              </span>
            </div>

            <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3 max-w-xl">
              {ABOUT.stats.map((s, i) => {
                const Icon = STAT_ICONS[i % STAT_ICONS.length];
                return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="luxury-panel flex items-center gap-3 border border-brand-border/70 rounded-2xl p-4"
                  data-testid={`about-stat-${i}`}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-olive/10 text-brand-olive">
                    <Icon size={20} strokeWidth={1.7} />
                  </div>
                  <div>
                    <div className="text-2xl lg:text-3xl text-brand-olive font-semibold leading-none">
                      {i === 2 ? "10" : s.value}
                    </div>
                    <div className="mt-1 text-sm text-brand-ink font-medium">
                      {i === 2 ? "سنوات ضمان إنشائي معتمد" : s.label}
                    </div>
                  </div>
                </motion.div>
                );
              })}
            </div>
          </motion.div>

          <div className="lg:col-span-6 order-1 lg:order-2 relative h-[360px] lg:h-[520px]">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="absolute inset-y-0 end-0 w-[80%] overflow-hidden shadow-2xl rounded-[2rem]"
            >
              <img
                src="/images/interior-living.jpg"
                alt="صالة المعيشة - تشطيب فاخر"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/18 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 }}
              className="hidden lg:block absolute bottom-10 start-0 w-[52%] h-[42%] overflow-hidden shadow-2xl border-[7px] border-brand-cream rounded-[1.5rem]"
            >
              <img
                src="/images/exterior-building.jpg"
                alt="واجهة تاون هاوس الشامخة"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.6 }}
              className="hidden lg:flex absolute top-6 start-6 bg-brand-cream/95 border border-brand-border px-4 py-3 items-center gap-3 shadow-md z-10 rounded-2xl"
            >
              <div className="w-2 h-2 bg-brand-olive rounded-full animate-pulse" />
              <span className="text-xs text-brand-ink">
                إطلاق مبيعات المرحلة الأولى
              </span>
            </motion.div>

            <div className="hidden lg:block absolute -bottom-6 end-0 text-[10rem] font-serif text-brand-accent/15 leading-none select-none pointer-events-none">
              ٠١
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
